import { useEffect, useRef, type RefObject } from 'react';
import { STATUS_SWATCH, unitStatus, type Unit } from '@alrimal/contracts';
import { useLocale } from '../LocaleContext.tsx';
import type { MessageKey } from '../i18n.ts';

/**
 * Cursor-following identity chip for the hovered masterplan unit.
 *
 * The competitor's masterplan is a pre-rendered image where nothing responds
 * at all; here a building answers before it is even tapped. DOM, not canvas —
 * the Arabic text shapes, selects and screen-reads like any other text (§1).
 * Touch never hovers, so on a phone this simply never mounts.
 */
export function UnitTooltip({
  unit,
  unitId,
  pointer,
}: {
  unit: Unit | null;
  unitId: string;
  pointer: RefObject<{ x: number; y: number }>;
}) {
  const { t } = useLocale();
  const ref = useRef<HTMLDivElement | null>(null);

  // Follow the pointer imperatively: the hover event fires once per unit, but
  // the cursor keeps moving across it, and re-rendering React per mousemove
  // would be the wrong tool for a transform update.
  useEffect(() => {
    let frame = 0;
    const follow = () => {
      const element = ref.current;
      const point = pointer.current;
      if (element && point) {
        element.style.transform = `translate3d(${point.x}px, ${point.y}px, 0)`;
      }
      frame = requestAnimationFrame(follow);
    };
    follow();
    return () => cancelAnimationFrame(frame);
  }, [pointer]);

  const status = unit ? unitStatus(unit) : null;
  const productKey: MessageKey | null = unit
    ? unit.productType === 'villa_with_apartment'
      ? 'unit.villa'
      : 'unit.townhouse'
    : null;

  // aria-hidden: mouse-only affordance. The tap opens the full unit panel,
  // which is where assistive tech gets the same facts and more.
  return (
    <div className="unit-tooltip" ref={ref} aria-hidden="true">
      <div className="unit-tooltip__card">
        <span className="unit-tooltip__id num">{unitId}</span>
        {productKey ? (
          <span className="unit-tooltip__product">{t(productKey)}</span>
        ) : null}
        {status ? (
          <span className="unit-tooltip__status">
            <span
              className="unit-tooltip__dot"
              style={{ background: STATUS_SWATCH[status] }}
              aria-hidden="true"
            />
            {t(`status.${status}` as MessageKey)}
          </span>
        ) : null}
      </div>
    </div>
  );
}
